import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Head from '../../../layout/head/Head';
import Content from '../../../layout/content/Content';
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  DataTableHead,
  DataTableItem,
  DataTableRow,
  Icon,
  PaginationComponent,
} from '../../../components/Component';
import { useNavigate, Link } from 'react-router-dom';
import AuthTenantLoader from '../../loaders/AuthTenantLoader';
import AuthTenantPageError from '../../error/AuthTenantPageError';
import AuthTenantNoData from '../../error/AuthTenantNoData';
import {
  RETRIEVE_TENANT_ORDERS_LIST_API,
} from '../../../api/api_data';
import {
  fail_retrieve_tenant_orders_list,
  loading_retrieve_tenant_orders_list,
  reset_retrieve_tenant_orders_list,
  success_retrieve_tenant_orders_list,
} from '../../../reducers/auth_tenant/retrieve_tenant_orders_list_Slice';
import {
  RETRIEVE_TENANT_ORDERS_LIST_ERROR_MSG,
  RETRIEVE_TENANT_ORDERS_LIST_SUCCESS_MSG,
} from '../../../utils/error_success_messages';
import * as app_consts from "../../../utils/app_consts";
import { tenant_auth_actions_helper } from '../../../actions/actions_helper';
import { addThreeDots, formatNumber, formatToSentenceCase, messageToast } from '../../../utils/Utils';
import moment from 'moment';
import {
  Badge,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  UncontrolledDropdown,
} from 'reactstrap';
import BackDateOrderModal from '../../../components/modal/BackDateOrderModal';
import CardTabs from '../../components/cards/CardTabs';

const OrderList = ({ tenant_info, user_info }) => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [activeCardTab, setActiveCardTab] = useState(
    () => localStorage.getItem("orders_activeTab") || "All"
  );
  const [error, setError] = useState("");
  const [orders, setOrders] = useState([]);
  const [current_page_data, setCurrentPageData] = useState([]);
  const [itemPerPage] = useState(10);
  const [sm, updateSm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(
    () => Number(localStorage.getItem("orders_currentPage")) || 1
  );
  const [back_date_modal, setBackDateModal] = useState(false);

  // Pagination
  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = current_page_data?.slice(indexOfFirstItem, indexOfLastItem);

  // Redux listener
  const retrieve_tenant_orders_list_listener = useSelector(
    (state) => state.retrieve_tenant_orders_list.retrieve_tenant_orders_list
  );

  useEffect(() => {
    retrieve_orders_list();
  }, []);

  const retrieve_orders_list = () => {
    setError("");
    setLoading(true);
    dispatch(
      tenant_auth_actions_helper(
        loading_retrieve_tenant_orders_list,
        success_retrieve_tenant_orders_list,
        fail_retrieve_tenant_orders_list,
        RETRIEVE_TENANT_ORDERS_LIST_ERROR_MSG,
        RETRIEVE_TENANT_ORDERS_LIST_SUCCESS_MSG,
        app_consts.GET,
        `/${tenant_info?.url_prefix}/${RETRIEVE_TENANT_ORDERS_LIST_API}`
      )
    );
  };

  useEffect(() => {
    if (retrieve_tenant_orders_list_listener.status !== app_consts.IDLE)
    {
      if (retrieve_tenant_orders_list_listener.status === app_consts.LOADING)
      {
        setLoading(true);
        setError("");
      } else if (retrieve_tenant_orders_list_listener.status === app_consts.FAIL)
      {
        setLoading(false);
        setError(retrieve_tenant_orders_list_listener.message);
        messageToast(retrieve_tenant_orders_list_listener.message, app_consts.FAIL);
      } else if (retrieve_tenant_orders_list_listener.status === app_consts.SUCCESS)
      {
        setOrders(retrieve_tenant_orders_list_listener.data);
        setCurrentPageData(retrieve_tenant_orders_list_listener.data);
        setLoading(false);
      }
      dispatch(reset_retrieve_tenant_orders_list());
    }
  }, [retrieve_tenant_orders_list_listener, dispatch]);

  // Save page to localStorage
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    localStorage.setItem("orders_currentPage", pageNumber);
  };

  // Filtering by search
  useEffect(() => {
    let filtered = [];

    // Step 1: Tab filter
    if (activeCardTab.startsWith("All"))
    {
      filtered = [...orders];
    } else
    {
      const statusLabel = activeCardTab.split(" (")[0]; // remove count
      filtered = orders.filter(
        (o) => formatToSentenceCase(o?.status) === statusLabel
      );
    }

    // Step 2: Search filter
    if (searchTerm)
    {
      filtered = filtered.filter((item) => {
        const combinedParams = [
          item?.sales_rep?.name,
          item.order_ref_number,
          item.order_name,
          item?.inserted_at ? moment(item?.inserted_at).format("YYYY-MM-DD HH:mm:ss") : "--",
          item.status?.replace(/_/g, " ").toUpperCase(),
        ]
          .join(" ")
          .toLowerCase();
        return combinedParams.includes(searchTerm.toLowerCase());
      });
    }

    // Step 3: Save to state
    setCurrentPageData(filtered);
    setCurrentPage(1);

    // Persist tab
    localStorage.setItem("orders_activeTab", activeCardTab);
  }, [activeCardTab, searchTerm, orders]);

  const toggle_back_date_modal = () => {
    setBackDateModal(!back_date_modal);
  };

  // ✅ Build tabs dynamically
  const statusCounts = orders.reduce((acc, order) => {
    const status = order?.status ? formatToSentenceCase(order?.status) : "Unknown";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const tabs = [
    { tabName: `All (${orders.length})` },
    ...Object.entries(statusCounts).map(([status, count]) => ({
      tabName: `${status} (${count})`,
    })),
  ];

  // ✅ Filter items by active tab & persist it
  useEffect(() => {
    if (activeCardTab.startsWith("All"))
    {
      setCurrentPageData(orders);
    } else
    {
      const statusLabel = activeCardTab.split(" (")[0]; // remove count
      const filtered = orders.filter(
        (o) => formatToSentenceCase(o?.status) === statusLabel
      );
      setCurrentPageData(filtered);
    }
    setCurrentPage(1);
    localStorage.setItem("orders_activeTab", activeCardTab);
  }, [activeCardTab, orders]);

  useEffect(() => {
    setSearchTerm("");
  }, [activeCardTab]);

  return (
    <>
      <Head title={tenant_info?.name ? `${tenant_info.name} Order` : "Orders"} />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Orders</BlockTitle>
              <BlockDes className="text-soft">
                <p>{!(loading || error) && `Orders ${orders?.length}`}</p>
              </BlockDes>
              {!(loading) && (
                <div className="mt-2">
                  <Link
                    onClick={(e) => {
                      e.preventDefault();
                      retrieve_orders_list();
                    }}
                  >
                    <Button color="light" outline className="bg-white d-none d-sm-inline-flex">
                      <span>Refresh List</span>
                    </Button>
                  </Link>
                </div>
              )}
            </BlockHeadContent>
            {!(loading || error) && (
              <BlockHeadContent>
                <div className="toggle-wrap nk-block-tools-toggle">
                  <a
                    href="#more"
                    className="btn btn-icon btn-trigger toggle-expand me-n1"
                    onClick={(ev) => {
                      ev.preventDefault();
                      updateSm(!sm);
                    }}
                  >
                    <Icon name="more-v"></Icon>
                  </a>
                  <div className="toggle-expand-content" style={{ display: sm ? "block" : "none" }}>
                    <ul className="nk-block-tools g-3">
                      <li>
                        <div className="form-control-wrap">
                          <div className="form-icon form-icon-right">
                            <Icon name="search"></Icon>
                          </div>
                          <Input
                            type="text"
                            className="form-control"
                            id="default-04"
                            value={searchTerm}
                            onChange={e => {
                              setSearchTerm(e.target.value);
                              setCurrentPage(1);
                            }}
                            placeholder="Search Orders..."
                          />
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </BlockHeadContent>
            )}
          </BlockBetween>
        </BlockHead>
        {loading ? (
          <AuthTenantLoader tenant_info={tenant_info} loading_message="Loading Orders..." />
        ) : error ? (
          <AuthTenantPageError tenant_info={tenant_info} error_message={error} />
        ) : orders && orders.length > 0 ? (
          <>
            <Block>
              <CardTabs
                cardTabs={tabs}
                activeCardTab={activeCardTab}
                setActiveCardTab={setActiveCardTab}
              />
            </Block>
            <Block>
              <div className="nk-tb-list is-separate is-medium mb-3">
                <DataTableHead className="nk-tb-item">
                  <DataTableRow><span className="sub-text">Order</span></DataTableRow>
                  <DataTableRow><span className="sub-text">Sales Rep / Employee</span></DataTableRow>
                  <DataTableRow><span className="sub-text">Behalf Of</span></DataTableRow>
                  <DataTableRow><span className="sub-text">No Of Products</span></DataTableRow>
                  <DataTableRow size="lg"><span className="sub-text">Date Created</span></DataTableRow>
                  <DataTableRow size="md"><span className="sub-text">Order Date Type</span></DataTableRow>
                  <DataTableRow size="md"><span className="sub-text">Status</span></DataTableRow>
                  <DataTableRow><span className="sub-text">Action</span></DataTableRow>
                </DataTableHead>

                {currentItems.length > 0 &&
                  currentItems.map((item) => (
                    <DataTableItem key={item.id}>
                      <DataTableRow>
                        <Link to={`/${tenant_info.url_prefix}/orders/${item?.order_ref_number}`}>
                          <div className="user-card">
                            <div className="user-info">
                              <span className="tb-lead">
                                {addThreeDots(item?.order_name || "--", 30)}
                              </span>
                              <span>{item?.order_ref_number || "--"}</span>
                            </div>
                          </div>
                        </Link>
                      </DataTableRow>
                      <DataTableRow>
                        <span className="tb-lead">{item?.sales_rep?.name || "--"}</span>
                      </DataTableRow>
                      <DataTableRow>
                        <span className="tb-lead">
                          {item?.behalf_of === app_consts.SELF
                            ? item?.sales_rep?.name
                            : item?.agency?.name}
                        </span>
                      </DataTableRow>
                      <DataTableRow>
                        <div className="user-card">
                          <div className="user-info">
                            <span className="tb-lead">{item?.no_of_products || "0"}</span>
                            <span>{item?.total_value ? `${formatNumber(item?.total_value)} ZMW` : ""}</span>
                          </div>
                        </div>
                      </DataTableRow>
                      <DataTableRow size="md">
                        <span className="tb-lead">
                          {item?.inserted_at
                            ? moment(item?.inserted_at).format("YYYY-MM-DD HH:mm:ss")
                            : "--"}
                        </span>
                      </DataTableRow>
                      <DataTableRow size="md">
                        {item?.order_date_type
                          ? formatToSentenceCase(item?.order_date_type)?.toUpperCase()
                          : "--"}
                      </DataTableRow>
                      <DataTableRow size="md">
                        <Badge
                          color={
                            item?.status === app_consts.APPROVED_LOWER ||
                              item?.status === app_consts.DISPATCHED_LOWER
                              ? "success"
                              : "warning"
                          }
                          className="badge-dot"
                        >
                          {item?.status
                            ? formatToSentenceCase(item?.status)?.toUpperCase()
                            : "--"}
                        </Badge>
                      </DataTableRow>
                      <DataTableRow>
                        <Link to={`/${tenant_info.url_prefix}/orders/${item?.order_ref_number}`}>
                          <Button color="primary" size="sm" className="btn btn-dim">
                            View
                          </Button>
                        </Link>
                      </DataTableRow>
                    </DataTableItem>
                  ))}
              </div>
              {currentItems.length > 0 && (
                <div className="mt-3">
                  <PaginationComponent
                    itemPerPage={itemPerPage}
                    totalItems={current_page_data?.length}
                    paginate={paginate}
                    currentPage={currentPage}
                  />
                </div>
              )}
            </Block>
            <BackDateOrderModal
              isOpen={back_date_modal}
              toggle={toggle_back_date_modal}
              tenant_info={tenant_info}
              user_info={user_info}
            />
          </>
        ) : (
          <AuthTenantNoData tenant_info={tenant_info} no_data_message="No Orders Found" />
        )}
      </Content>
    </>
  );
};

export default OrderList;
